// bumpers: about five seconds over the whole frame. The band wipes in, the plate drops on the sting, the title and
// clock come up, her line plays, "up next" slides up, and it fades out as the next screen takes over underneath.
// Music ducks 6 dB for the length of it. With no voice line it's bell and plate only (the outros).
// Lines and stings come from the build (scripts/audio.mjs): rhl/audio/*.wav and rhl/audio/voice.json.
import { escapeHtml } from '../utils/string.mjs';
import { duck, isMuted } from './music.mjs';
import { time } from './format.mjs';
import { screenIcon } from './icons.mjs';

const { brand } = window.RHL;
const AUDIO = 'rhl/audio';
const DUCK_DB = 6;

let lines = {};
const linesReady = fetch(`${AUDIO}/voice.json`)
	.then((r) => r.json())
	.then((body) => { lines = Object.fromEntries(body.lines.map((l) => [l.id, l])); })
	.catch((error) => console.warn('Russian Hill Local bumpers: no voice manifest', error.message));

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Sound goes through one Web Audio context (Oct 26). A phone (iPhone above all) lets a page play sound only from a
// player a tap has started; a new Audio element per line can be refused even after earlier taps, which is how a
// Markets intro went silent on Jared's phone while News played. The first tap, click or key unlocks the context,
// and every sting and line after it plays on time. Files are fetched and decoded once.
let context = null;
const audioContext = () => {
	if (!context) {
		const Context = window.AudioContext || window.webkitAudioContext;
		if (Context) context = new Context();
	}
	return context;
};
const decoded = new Map();
const load = (src) => {
	if (!decoded.has(src)) {
		decoded.set(src, fetch(src)
			.then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
			.then((data) => new Promise((resolve, reject) => { audioContext().decodeAudioData(data, resolve, reject); }))
			.catch(() => { decoded.delete(src); return null; }));
	}
	return decoded.get(src);
};
const unlock = () => {
	const ctx = audioContext();
	if (!ctx) return;
	if (ctx.state !== 'running') ctx.resume().catch(() => {});
	// a silent sound started inside the tap is what opens audio on iOS
	const source = ctx.createBufferSource();
	source.buffer = ctx.createBuffer(1, 1, 22050);
	source.connect(ctx.destination);
	source.start(0);
};
['pointerdown', 'touchend', 'click', 'keydown'].forEach((type) => document.addEventListener(type, unlock, { capture: true, passive: true }));

// plays a file and resolves when it ends; with sound blocked (no tap yet), muted, or a file that won't load in time,
// it resolves after the expected length, so the picture keeps time either way
const play = async (src, expectedSeconds) => {
	const ctx = audioContext();
	const fallback = () => wait((expectedSeconds + 0.3) * 1000);
	if (!ctx || isMuted()) return fallback();
	// resume() never settles until a tap, click or key (Chrome): wait briefly, not forever (Oct 27: with no tap yet a
	// voiced intro held the bumper on screen and the feature never started)
	if (ctx.state !== 'running') await Promise.race([ctx.resume().catch(() => {}), wait(250)]);
	if (ctx.state !== 'running') return fallback();
	const buffer = await Promise.race([load(src), wait(1500).then(() => null)]);
	if (!buffer) return fallback();
	const source = ctx.createBufferSource();
	source.buffer = buffer;
	source.connect(ctx.destination);
	return new Promise((resolve) => {
		const done = setTimeout(resolve, (buffer.duration + 0.5) * 1000);
		source.onended = () => { clearTimeout(done); resolve(); };
		source.start();
	});
};

// the stings and the lines the clock uses most, decoded ahead so the first bumper doesn't wait on the network
linesReady.then(() => {
	const { voice } = window.RHL.clock;
	const ids = [...Object.values(voice.featureIntros).flat(), ...voice.stationId.lines];
	['bell-2', 'bell-1', 'foghorn'].forEach((sting) => load(`${AUDIO}/${sting}.wav`));
	ids.filter((id) => lines[id]).forEach((id) => load(`${AUDIO}/voice/${id}.wav`));
});

const STINGS = { 'bell-2': 0.9, 'bell-1': 0.5, foghorn: 3.1 };

let overlay = null;
const element = () => {
	if (overlay) return overlay;
	overlay = document.createElement('div');
	overlay.className = 'rhl-bumper';
	overlay.innerHTML = `<div class="bumper-band"></div>
		<div class="bumper-plate rhl-plate">${brand.plate.lines.map(escapeHtml).join('<br>')}</div>
		<div class="bumper-title"><div class="top"></div><div class="bottom"></div></div>
		<div class="bumper-clock"></div>
		<div class="bumper-next"><span class="label">Up next</span> <span class="what"></span></div>`;
	document.querySelector('#container')?.append(overlay);
	return overlay;
};

let playing = false;
const isPlaying = () => playing;

// spec: { title: [top, bottom], line: voice id or null, sting: 'bell-2' | 'bell-1' | 'foghorn', upNext, alert,
//         onCovered: called once the frame is covered, to switch the screen underneath,
//         hold: ms "up next" stays before the cut, fade: ms of the fade off (the outros, Oct 26: they felt rushed) }
const playBumper = async ({
	title = [brand.name, ''], line = null, sting = 'bell-2', upNext = null, alert = false, onCovered = null,
	hold = null, fade = 350,
} = {}) => {
	await linesReady;
	const el = element();
	const voice = line ? lines[line] : null;
	playing = true;
	duck(DUCK_DB);

	el.querySelector('.bumper-title .top').textContent = title[0] ?? '';
	el.querySelector('.bumper-title .bottom').textContent = title[1] ?? '';
	el.querySelector('.bumper-clock').textContent = time(new Date());
	el.querySelector('.bumper-next').hidden = !upNext;
	el.querySelector('.bumper-next .what').innerHTML = upNext ? `${screenIcon(upNext)}${upNext}` : '';
	el.className = `rhl-bumper${alert ? ' alert' : ''}${voice ? '' : ' short'}`;
	el.classList.add('on');

	// 0: fade in and wipe the band; 1: the plate drops on the sting; 2: title and clock
	await wait(30);
	el.classList.add('step-1');
	await wait(380);
	// no sting: the plate drops in silence (outros, and an intro with no line)
	const stingDone = sting ? play(`${AUDIO}/${sting}.wav`, STINGS[sting] ?? 1) : wait(300);
	el.classList.add('step-2');
	await wait(250);
	el.classList.add('step-3');
	// the line starts as the bell rings out (the foghorn gets most of its blast first)
	await wait({ foghorn: 1800, null: 150 }[sting] ?? 450);
	const voiceDone = voice ? play(`${AUDIO}/voice/${voice.id}.wav`, voice.seconds) : stingDone;
	const voiceMs = voice ? voice.seconds * 1000 : 0;
	if (upNext) {
		await wait(Math.max(300, voiceMs - 900));
		el.classList.add('step-4');
	}
	await voiceDone;
	await wait(hold ?? (voice ? 700 : 900));
	// covered: the screen underneath changes now, then the bumper fades off it
	onCovered?.();
	el.style.transitionDuration = fade === 350 ? '' : `${fade}ms`;
	el.classList.remove('on');
	duck(0);
	await wait(fade);
	el.style.transitionDuration = '';
	el.className = 'rhl-bumper';
	playing = false;
};

export {
	playBumper,
	isPlaying,
	linesReady,
};
